import Modal from '@src/components/common/Modal';
import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import { getSpeakerColor } from '@src/services/SpeakerManagementService';
import { useDubbingStore } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { type CSSProperties, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import SpeakerAIVoiceMapper from './SpeakerAIVoiceMapper';
import SpeakerVoiceAuditionArea from './SpeakerVoiceAuditionArea';
import { useSpeakerAiVoices } from './useSpeakerAiVoices';
import { useVoiceActivation } from './useVoiceActivation';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

interface ManageAiVoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  speakerId: string;
  speakerName: string;
  /** Speaker headshot for the header avatar; falls back to initials. */
  speakerHeadshotUrl?: string | null;
  /**
   * The speaker's colour, the same one their avatar wears everywhere else
   * (dub modal, speaker panel). Falls back to the shared id-hashed palette
   * colour when the launcher only has a bare DatoSpeaker (PLAT-11581).
   */
  speakerColor?: string;
  /** Open on By Language with this language in view (PLAT-11581). */
  initialLanguageId?: string;
}

// Fixed-height panel so every tab is the same height (matching the first tab);
// each tab's body scrolls internally rather than resizing the modal. Capped to
// the viewport with generous top/bottom margin.
const CONTENT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: 'min(900px, calc(100vw - 32px))',
  height: 'min(640px, calc(100vh - 128px))',
  overflow: 'hidden',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  padding: '24px',
};

// The per-speaker AI Voice management surface (DE4/DI19), launched from a
// speaker's three-dot menu. A speaker-scoped twin of Settings → AI Voices:
// pick the speaker's voices (Voices tab) and set per-language defaults
// (Languages tab). Editing only — the speaker pop-over's "AI Voices" tab is the
// view-and-listen surface (PLAT-10502/10503).
const ManageAiVoicesModal = observer(
  ({
    isOpen,
    onClose,
    speakerId,
    speakerName,
    speakerHeadshotUrl,
    speakerColor,
    initialLanguageId,
  }: ManageAiVoicesModalProps) => {
    const avatarColor = speakerColor ?? getSpeakerColor(speakerId, 0);
    const dubbingStore = useDubbingStore();
    const { organization, activatedVoices } = useVoiceActivation();
    const {
      aiVoiceIds,
      languagePrefs,
      loading,
      busy,
      addVoice,
      removeVoice,
      moveVoice,
      setLanguageDefault,
    } = useSpeakerAiVoices(isOpen ? speakerId : null);

    // Belt-and-suspenders: catalog is eagerly hydrated at workspace init, but
    // hydrate() is idempotent per showcase so this is a no-op if already loaded.
    useEffect(() => {
      if (isOpen && organization?.id) dubbingStore.hydrate(organization.id);
    }, [isOpen, organization?.id, dubbingStore]);

    // Resolve the speaker's ordered voice IDs to full records from the catalog
    // (all active voices), preserving order; drop any that no longer resolve.
    const orderedVoices = useMemo(
      () =>
        aiVoiceIds
          .map(id => dubbingStore.voiceById(id))
          .filter((v): v is AiVoiceRecord => Boolean(v)),
      [aiVoiceIds, dubbingStore, dubbingStore.catalog],
    );

    // Only workspace-activated voices not already selected can be added.
    const addablePool = useMemo(
      () => activatedVoices.filter(v => !aiVoiceIds.includes(v.id)),
      [activatedVoices, aiVoiceIds],
    );

    const languages = useMemo(
      () => organization?.supportedLanguages ?? [],
      [organization?.supportedLanguages],
    );

    // Find & Audition tab browses the whole activated pool; membership + toggle
    // reflect which of those voices are on the speaker.
    const memberIds = useMemo(() => new Set(aiVoiceIds), [aiVoiceIds]);
    const toggleSpeakerVoice = useCallback(
      (voiceId: string) => {
        if (aiVoiceIds.includes(voiceId)) removeVoice(voiceId);
        else addVoice(voiceId);
      },
      [aiVoiceIds, addVoice, removeVoice],
    );

    if (!isOpen) return null;

    return (
      <Modal isOpen={isOpen} closeModal={onClose} contentStyle={CONTENT_STYLE}>
        <Header>
          <SpeakerAvatar
            name={speakerName}
            color={avatarColor}
            headshotUrl={speakerHeadshotUrl}
            size={40}
            initialsLength={2}
          />
          <div className="text">
            <h2>Manage AI Voices</h2>
            <p>{speakerName}</p>
          </div>
          {/* There is no Save button because every change persists at once;
              say so where a Save button would be looked for (PLAT-10830). */}
          <SaveStatus aria-live="polite">
            {busy ? 'Saving…' : 'Changes save automatically'}
          </SaveStatus>
        </Header>
        <Body>
          {loading ? (
            <LoadingRow>
              <SimpleLoadingSpinner />
            </LoadingRow>
          ) : (
            <SpeakerAIVoiceMapper
              orderedVoices={orderedVoices}
              addablePool={addablePool}
              hasActivatedVoices={activatedVoices.length > 0}
              languages={languages}
              languagePrefs={languagePrefs}
              busy={busy}
              onAddVoice={addVoice}
              onRemoveVoice={removeVoice}
              onMoveVoice={moveVoice}
              onSetLanguageDefault={setLanguageDefault}
              speakerName={speakerName}
              workspaceName={organization?.title}
              initialLanguageId={initialLanguageId}
              auditionArea={
                <SpeakerVoiceAuditionArea
                  voices={activatedVoices}
                  memberIds={memberIds}
                  onToggleMember={toggleSpeakerVoice}
                  busy={busy}
                />
              }
            />
          )}
        </Body>
      </Modal>
    );
  },
);

export default ManageAiVoicesModal;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-right: 32px;
  flex-shrink: 0;

  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  h2 {
    margin: 0;
    font-size: var(--text-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--foreground);
  }
  p {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-muted);
  }
`;

const SaveStatus = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  white-space: nowrap;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* Keep a long language/voice list inside the bounded modal rather than
     letting it grow to the viewport edges. */
  overflow-y: auto;
`;

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;
